"use client"

import { useEffect, useRef, useState, type ReactNode } from "react"

type DeferredHydrationProps = {
  children: ReactNode
  /** Reserve space before the deferred subtree mounts (avoids CLS). */
  minHeight?: string
  rootMargin?: string
}

/**
 * Mount children only when the placeholder nears the viewport so below-fold
 * client components (footers) don't compete with above-fold hydration.
 */
export function DeferredHydration({
  children,
  minHeight = "280px",
  rootMargin = "400px",
}: DeferredHydrationProps) {
  const ref = useRef<HTMLDivElement>(null)
  const [shouldMount, setShouldMount] = useState(false)

  useEffect(() => {
    const element = ref.current
    if (!element || shouldMount) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setShouldMount(true)
          observer.disconnect()
        }
      },
      { rootMargin },
    )

    observer.observe(element)
    return () => observer.disconnect()
  }, [rootMargin, shouldMount])

  return (
    <div ref={ref} style={{ minHeight: shouldMount ? undefined : minHeight }}>
      {shouldMount ? children : null}
    </div>
  )
}
